import { Suspense, useCallback } from "react";
import { View } from "react-native";
import {
  createNativeStackNavigator,
  type NativeStackScreenProps,
} from "@react-navigation/native-stack";
import type { CareCalendarRouteParams, CareStackParamList } from "../types";
import DocumentsScreen from "./DocumentsScreen";
import CryoVaultScreen from "./CryoVaultScreen";
import EmbryoReportScreen from "./EmbryoReportScreen";
import CycleDetailScreen from "./CycleDetailScreen";
import MetricDetailPreviewScreen from "./MetricDetailPreviewScreen";
import PastCycleDetailScreen from "./PastCycleDetailScreen";
import AllPastCyclesScreen from "./AllPastCyclesScreen";
import AllLabResultsScreen from "./AllLabResultsScreen";
import MedicalHistoryScreen from "./MedicalHistoryScreen";

// Medical history sub-flow navigators
import { BasicInfoNavigator } from "@louise/medical-history/basic-info/BasicInfoNavigator";
import { InfertilityNavigator } from "@src/packages/medical-history/fertility/infertility/InfertilityNavigator";
import { FertilityTestingNavigator } from "@src/packages/medical-history/fertility/fertility-testing/FertilityTestingNavigator";
import { FertilityTreatmentsNavigator } from "@src/packages/medical-history/fertility/fertility-treatments/FertilityTreatmentsNavigator";
import { MedicationsVaccinesNavigator } from "@src/packages/medical-history/medical/medications-vaccines/MedicationsVaccinesNavigator";
import { AllergiesNavigator } from "@src/packages/medical-history/medical/allergies/AllergiesNavigator";
import { SurgeriesNavigator } from "@src/packages/medical-history/medical/surgeries/SurgeriesNavigator";
import ExposureNavigator from "@src/packages/medical-history/lifestyle/exposure/ExposureNavigator";
import { SubstancesNavigator } from "@louise/medical-history/lifestyle/substances/SubstancesNavigator";
import { DietSupplementsNavigator } from "@src/packages/medical-history/lifestyle/diet-supplements/DietSupplementsNavigator";
import { MenstrualHealthNavigator } from "@src/packages/medical-history/gynae/menstrual-health/MenstrualHealthNavigator";
import { DiseaseHistoryNavigator } from "@src/packages/medical-history/medical/disease-history/DiseaseHistoryNavigator";
import { SexualHealthNavigator } from "@src/packages/medical-history/gynae/sexual-health/SexualHealthNavigator";
import { ContraceptionNavigator } from "@src/packages/medical-history/gynae/contraception/ContraceptionNavigator";
import { PregnancyNavigator } from "@src/packages/medical-history/gynae/pregnancies/PregnancyNavigator";
import { SleepActivityNavigator } from "@louise/medical-history/lifestyle/sleep-activity/SleepActivityNavigator";
import { FamilyHistoryNavigator } from "@src/packages/medical-history/family-history/FamilyHistoryNavigator";
import { PastCyclesScreen } from "@src/packages/medical-history/fertility/PastCyclesScreen/PastCyclesScreen";
import {
  MedicalHistoryThemeProvider,
  MOON_THEME,
} from "@src/packages/medical-history/theme/MedicalHistoryTheme";
import { SectionReviewScreen } from "@src/packages/medical-history/review/SectionReviewScreen";
import CareReviewConceptScreen, {
  getCareReviewConceptVariant,
  getDevSuggestedCareSurface,
} from "./care/suggested/CareReviewConceptScreen";

const Stack = createNativeStackNavigator<CareStackParamList>();

type OverviewProps = NativeStackScreenProps<CareStackParamList, "CareOverview">;

function CareOverviewRoute(props: OverviewProps) {
  const devSuggestedCareSurface = getDevSuggestedCareSurface();
  const suggestedVariant =
    getCareReviewConceptVariant(devSuggestedCareSurface) ??
    "egg-freezing-active";
  const showFixtureData = devSuggestedCareSurface !== null;
  const onCalendarStateChange = useCallback(
    (state: CareCalendarRouteParams) => {
      props.navigation.setParams(state);
    },
    [props.navigation],
  );

  return (
    <CareReviewConceptScreen
      variant={suggestedVariant}
      navigation={props.navigation}
      initialAppointmentId={props.route.params?.appointmentId}
      initialFocus={props.route.params?.focus}
      initialCalendarState={props.route.params}
      onCalendarStateChange={onCalendarStateChange}
      showFixtureData={showFixtureData}
    />
  );
}

const MODAL_OPTIONS = {
  headerShown: false,
  presentation: "modal" as const,
  animation: "slide_from_right" as const,
  contentStyle: { backgroundColor: "transparent" },
};

export default function CareNavigator() {
  return (
    <MedicalHistoryThemeProvider theme={MOON_THEME}>
      <Stack.Navigator
        screenOptions={{ headerShown: false }}
        screenLayout={({ children }) => (
          <Suspense fallback={<View />}>{children}</Suspense>
        )}
      >
        <Stack.Screen name="CareOverview" component={CareOverviewRoute} />
        <Stack.Screen
          name="CycleDetail"
          component={CycleDetailScreen}
          options={{
            statusBarStyle: "light",
            statusBarBackgroundColor: "#1A0A02",
            contentStyle: { backgroundColor: "#1A0A02" },
          }}
        />
        <Stack.Screen
          name="MetricDetail"
          component={MetricDetailPreviewScreen}
        />
        <Stack.Screen
          name="PastCycleDetail"
          component={PastCycleDetailScreen}
        />
        <Stack.Screen name="AllPastCycles" component={AllPastCyclesScreen} />
        <Stack.Screen name="Documents" component={DocumentsScreen} />
        <Stack.Screen name="CryoVault" component={CryoVaultScreen} />
        <Stack.Screen name="EmbryoReport" component={EmbryoReportScreen} />
        <Stack.Screen name="AllLabResults" component={AllLabResultsScreen} />
        <Stack.Screen name="MedicalHistory" component={MedicalHistoryScreen} />
        <Stack.Screen name="SectionReview" component={SectionReviewScreen} />

        {/* Medical history sub-flows */}
        <Stack.Screen
          name="BasicInfoFlow"
          component={BasicInfoNavigator}
          options={MODAL_OPTIONS}
        />
        <Stack.Screen
          name="InfertilityFlow"
          component={InfertilityNavigator}
          options={MODAL_OPTIONS}
        />
        <Stack.Screen
          name="FertilityTestingFlow"
          component={FertilityTestingNavigator}
          options={MODAL_OPTIONS}
        />
        <Stack.Screen
          name="FertilityTreatmentsFlow"
          component={FertilityTreatmentsNavigator}
          options={MODAL_OPTIONS}
        />
        <Stack.Screen
          name="MedicationsVaccinesFlow"
          component={MedicationsVaccinesNavigator}
          options={MODAL_OPTIONS}
        />
        <Stack.Screen
          name="AllergiesFlow"
          component={AllergiesNavigator}
          options={MODAL_OPTIONS}
        />
        <Stack.Screen
          name="SurgeriesFlow"
          component={SurgeriesNavigator}
          options={MODAL_OPTIONS}
        />
        <Stack.Screen
          name="ExposureFlow"
          component={ExposureNavigator}
          options={MODAL_OPTIONS}
        />
        <Stack.Screen
          name="SubstancesFlow"
          component={SubstancesNavigator}
          options={MODAL_OPTIONS}
        />
        <Stack.Screen
          name="DietSupplementsFlow"
          component={DietSupplementsNavigator}
          options={MODAL_OPTIONS}
        />
        <Stack.Screen
          name="MenstrualHealthFlow"
          component={MenstrualHealthNavigator}
          options={MODAL_OPTIONS}
        />
        <Stack.Screen
          name="DiseaseHistoryFlow"
          component={DiseaseHistoryNavigator}
          options={MODAL_OPTIONS}
        />
        <Stack.Screen
          name="SexualHealthFlow"
          component={SexualHealthNavigator}
          options={MODAL_OPTIONS}
        />
        <Stack.Screen
          name="ContraceptionFlow"
          component={ContraceptionNavigator}
          options={MODAL_OPTIONS}
        />
        <Stack.Screen
          name="PregnanciesFlow"
          component={PregnancyNavigator}
          options={MODAL_OPTIONS}
        />
        <Stack.Screen
          name="SleepActivityFlow"
          component={SleepActivityNavigator}
          options={MODAL_OPTIONS}
        />
        <Stack.Screen
          name="FamilyHistoryFlow"
          component={FamilyHistoryNavigator}
          options={MODAL_OPTIONS}
        />
        <Stack.Screen
          name="PastCycles"
          component={PastCyclesScreen}
          options={MODAL_OPTIONS}
        />
      </Stack.Navigator>
    </MedicalHistoryThemeProvider>
  );
}
